// Ops panel — operator self-service for monitoring & backups.
//
// Rendered inside the platform back-office (/admin/platform, operator-only).
// Two cards backed by the is_admin-gated ops endpoints:
//   • Telegram alerts — GET/PUT /api/admin/alerting. Shows the live status
//     (enabled / config source / chat / topic / token set) and lets the
//     operator change the group, topic or bot token WITHOUT a redeploy.
//     Saving sends a real test message and reports delivery (or the exact
//     Telegram error, e.g. TOPIC_CLOSED).
//   • Nightly DB backups — GET /api/admin/backups + POST .../run. Shows the
//     schedule/settings and on-disk generations, with a manual "run now".
//
// The API is the security boundary (admin-gated server-side); this UI is
// convenience for a non-technical operator.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { useT } from '../i18n';
import AlertTopics from './AlertTopics';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { BellRing, DatabaseBackup, RefreshCw, ShieldCheck } from 'lucide-react';

function fmtMb(bytes) {
  if (bytes === null || bytes === undefined) return '—';
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function OpsPanel() {
  const { t } = useT();
  const [alerting, setAlerting] = useState(null);
  const [backups, setBackups] = useState(null);
  const [runtime, setRuntime] = useState(null);
  const [form, setForm] = useState({ bot_token: '', chat_id: '', message_thread_id: '' });
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null); // {delivered, error}
  const [runningBackup, setRunningBackup] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, b, r] = await Promise.all([
        axios.get(`${API}/admin/alerting`),
        axios.get(`${API}/admin/backups`),
        axios.get(`${API}/admin/runtime`),
      ]);
      setAlerting(a.data);
      setBackups(b.data);
      setRuntime(r.data);
      setForm((prev) => ({
        ...prev,
        chat_id: a.data?.chat_id || '',
        message_thread_id: a.data?.message_thread_id || '',
      }));
    } catch {
      // Non-admins never reach this page; a transient error just leaves the
      // cards in their loading state until the next refresh.
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveAlerting = async () => {
    if (!form.chat_id.trim()) {
      toast.error(t('Chat ID is required'));
      return;
    }
    setSaving(true);
    setTestResult(null);
    try {
      const res = await axios.put(`${API}/admin/alerting`, {
        bot_token: form.bot_token.trim(),
        chat_id: form.chat_id.trim(),
        message_thread_id: form.message_thread_id.trim(),
        enabled: true,
      });
      setAlerting(res.data);
      setForm((prev) => ({ ...prev, bot_token: '' }));
      const delivered = !!res.data?.test_delivered;
      setTestResult({ delivered, error: res.data?.test_error || null });
      if (delivered) {
        toast.success(t('Test message delivered'));
      } else {
        toast.error(`${t('Send failed')}: ${res.data?.test_error || '—'}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Send failed'));
    } finally {
      setSaving(false);
    }
  };

  const runBackup = async () => {
    setRunningBackup(true);
    try {
      await axios.post(`${API}/admin/backups/run`);
      toast.success(t('Backup started — the result will arrive in the monitoring topic.'));
      setTimeout(load, 6000); // refresh the generation list once it lands
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Send failed'));
    } finally {
      setRunningBackup(false);
    }
  };

  const generations = backups?.generations || [];
  const newest = generations.length ? generations[generations.length - 1] : null;

  return (
    <div className="grid gap-4 lg:grid-cols-2" data-testid="ops-panel">
      <Card
        className={`lg:col-span-2 ${runtime?.production_readiness?.ready === false ? 'border-destructive' : ''}`}
        data-testid="ops-readiness-card"
      >
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" />
            {t('Production readiness')}
            <Badge variant={runtime?.production_readiness?.ready ? 'default' : 'destructive'}>
              {runtime?.production_readiness?.ready ? t('Ready') : t('Attention required')}
            </Badge>
          </CardTitle>
          <CardDescription>
            {t('Secret-free checks for runtime configuration and isolation controls.')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="grid gap-2 text-xs sm:grid-cols-2 lg:grid-cols-4">
            <span>{t('Environment')}: <b>{runtime?.production_readiness?.checks?.production_environment ? t('production') : t('development')}</b></span>
            <span>{t('Rate limiter')}: <b>{runtime?.rate_limit?.backend || '—'}</b></span>
            <span>{t('Access token TTL')}: <b>{runtime?.production_readiness?.access_token_ttl_minutes ?? '—'} min</b></span>
            <span>{t('Backend root')}: <b>{runtime?.production_readiness?.checks?.backend_running_as_root ? t('yes') : t('no')}</b></span>
          </div>
          {(runtime?.production_readiness?.blocking_checks || []).length > 0 ? (
            <p className="text-xs text-destructive">
              {t('Blocking checks')}: {(runtime.production_readiness.blocking_checks || []).join(', ')}
            </p>
          ) : null}
          {(runtime?.production_readiness?.warnings || []).length > 0 ? (
            <ul className="list-disc pl-5 text-xs text-muted-foreground">
              {runtime.production_readiness.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          ) : null}
        </CardContent>
      </Card>
      <Card data-testid="ops-alerting-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <BellRing className="w-4 h-4" />
            {t('Telegram alerts')}
            {alerting ? (
              <Badge
                variant={alerting.enabled ? 'default' : 'destructive'}
                data-testid="alerting-state"
              >
                {alerting.enabled ? t('Configured') : t('Not configured')}
              </Badge>
            ) : null}
          </CardTitle>
          <CardDescription>
            {t('Deploy + server-error notifications for the operator.')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {alerting ? (
            <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
              <span>
                {t('Source')}: <span className="font-mono">{alerting.source}</span>
              </span>
              <span>
                {t('Chat ID')}: <span className="font-mono">{alerting.chat_id || '—'}</span>
              </span>
              <span>
                {t('Topic')}: <span className="font-mono">{alerting.message_thread_id || '—'}</span>
              </span>
              <span>
                {t('Bot token')}:{' '}
                <span className="font-mono">{alerting.token_configured ? '•••' : '—'}</span>
              </span>
            </div>
          ) : null}
          <div className="grid gap-2 sm:grid-cols-3">
            <div className="space-y-1">
              <Label className="text-xs">{t('Bot token')}</Label>
              <Input
                type="password"
                placeholder="•••"
                value={form.bot_token}
                onChange={(e) => setForm((p) => ({ ...p, bot_token: e.target.value }))}
                data-testid="alerting-token-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t('Chat ID')}</Label>
              <Input
                placeholder="-100…"
                value={form.chat_id}
                onChange={(e) => setForm((p) => ({ ...p, chat_id: e.target.value }))}
                data-testid="alerting-chat-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t('Topic ID (optional)')}</Label>
              <Input
                placeholder="6"
                value={form.message_thread_id}
                onChange={(e) => setForm((p) => ({ ...p, message_thread_id: e.target.value }))}
                data-testid="alerting-topic-input"
              />
              <AlertTopics onPick={(id) => setForm((p) => ({ ...p, message_thread_id: id }))} />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            {t('Leave the token empty to keep the saved one.')}
          </p>
          <div className="flex items-center gap-3">
            <Button size="sm" onClick={saveAlerting} disabled={saving} data-testid="alerting-save">
              {t('Save & send test')}
            </Button>
            {testResult ? (
              <span
                className={`text-xs ${testResult.delivered ? 'text-emerald-500' : 'text-red-500'}`}
                data-testid="alerting-test-result"
              >
                {testResult.delivered
                  ? `✓ ${t('Test message delivered')}`
                  : `✗ ${testResult.error || t('Send failed')}`}
              </span>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <Card data-testid="ops-backups-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <DatabaseBackup className="w-4 h-4" />
            {t('Nightly DB backups')}
            {backups ? (
              <Badge
                variant={backups.enabled ? 'default' : 'destructive'}
                data-testid="backups-state"
              >
                {backups.enabled ? t('Enabled') : t('Disabled')}
              </Badge>
            ) : null}
          </CardTitle>
          <CardDescription>
            {t('A full copy of the database every night, kept on the persistent volume.')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {backups ? (
            <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
              <span>
                {t('Daily at')} {String(backups.hour_utc).padStart(2, '0')}:00 UTC
              </span>
              <span>
                {t('Copies kept')}: {backups.keep}
              </span>
              <span>
                {t('Stored')}: {generations.length}
              </span>
              {newest ? (
                <span data-testid="backups-newest">
                  {t('Latest')}: <span className="font-mono">{newest.name}</span> ·{' '}
                  {fmtMb(newest.bytes)}
                </span>
              ) : (
                <span data-testid="backups-newest">{t('No backups yet')}</span>
              )}
            </div>
          ) : null}
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={runBackup}
              disabled={runningBackup || !backups}
              data-testid="backups-run"
            >
              {t('Run backup now')}
            </Button>
            <Button size="sm" variant="outline" onClick={load} data-testid="ops-refresh">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
